import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Layers } from 'lucide-react';
import { TranspositionPanel, type TranspositionPanelProps } from './TranspositionPanel';

/**
 * Compact transposition chip for the builder's header row. Its incoming paths open
 * as a dropdown over the board, so neither the chip nor the list moves the board.
 */
export function TranspositionBadge(props: TranspositionPanelProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  if (!props.node || props.incomingPathCount < 2) return null;
  return (
    <div
      ref={root}
      className="transposition-insight"
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !open) return;
        event.preventDefault();
        setOpen(false);
        toggle.current?.focus();
      }}
      onBlur={(event) => {
        // Tabbing out closes the list; a click on its own text or padding does not.
        const next = event.relatedTarget as Node | null;
        if (next !== null && !event.currentTarget.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={toggle}
        type="button"
        className="transposition-badge"
        aria-expanded={open}
        aria-controls={id}
        title={`Reached via ${props.incomingPathCount} incoming paths`}
        onClick={() => setOpen(!open)}
      >
        <Layers size={15} aria-hidden="true" />
        <span>Transposition · {props.incomingPathCount} incoming paths</span>
        <ChevronDown size={15} aria-hidden="true" className={open ? 'rotate-180' : ''} />
      </button>
      <div id={id} hidden={!open} className="transposition-dropdown">
        <TranspositionPanel
          {...props}
          onNavigate={(lineId, ply) => {
            // Every listed path reaches this node, so the chip stays; keep focus on it.
            setOpen(false);
            toggle.current?.focus();
            props.onNavigate(lineId, ply);
          }}
        />
      </div>
    </div>
  );
}
